import { ChevronDownIcon } from '@radix-ui/react-icons';
import { Tooltip } from 'radix-ui';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useTranslation } from '~/app';
import { ChatDisplayItem } from '~/app/selectors';
import { cn } from '~/utils/styles';

import ChatEventMessage from './ChatEventMessage';
import ChatMessageGroup from './ChatMessageGroup';
import { ChatMessageListScrollContext } from './ChatMessageListScrollContext';

type MessageItem = ChatDisplayItem & {
  type: 'message';
  model: NonNullable<ChatDisplayItem['model']>;
  userId: string;
};

type DisplayBlock =
  | { kind: 'event'; item: ChatDisplayItem & { type: 'event' }; key: string }
  | { kind: 'group'; messages: MessageItem[]; key: string };

function buildDisplayBlocks(items: ChatDisplayItem[]): DisplayBlock[] {
  const blocks: DisplayBlock[] = [];
  let i = 0;
  while (i < items.length) {
    const item = items[i];
    if (item.type === 'event') {
      blocks.push({
        kind: 'event',
        item: item as ChatDisplayItem & { type: 'event' },
        key: item.id,
      });
      i += 1;
      continue;
    }
    if (item.type === 'message' && item.model && item.userId) {
      const group: MessageItem[] = [];
      while (i < items.length) {
        const it = items[i];
        if (it.type !== 'message' || !it.model || !it.userId) break;
        const m = it as MessageItem;
        if (group.length > 0) {
          const prev = group[group.length - 1];
          if (
            prev.userId !== m.userId ||
            prev.model.direction !== m.model.direction ||
            !!prev.isSpectator !== !!m.isSpectator
          ) {
            break;
          }
        }
        group.push(m);
        i += 1;
      }
      blocks.push({ kind: 'group', messages: group, key: group[0].id });
      continue;
    }
    i += 1;
  }
  return blocks;
}

const NEAR_BOTTOM_THRESHOLD_PX = 80;
const JUMP_BUTTON_OUT_ANIM_MS = 140;

export interface ChatMessageListProps {
  items: ChatDisplayItem[];
  players: Array<{ userId?: string; name?: string }> | undefined;
  /** Unknown player fallback when sender name is empty (e.g. t('history_unknown_player')). */
  unknownPlayerLabel: string;
  /** Index of the last incoming NEWMESSAGE that has been marked as read. */
  chatLastReadIncomingEventIndex: number;
  /** Unread incoming NEWMESSAGE count since last read. */
  unreadIncomingCount: number;
  /**
   * Called when the chat read index should advance.
   */
  onRequestMarkReadToIncomingEventIndex?: (eventLogIndex: number) => void;
}

const ChatMessageList: React.FC<ChatMessageListProps> = ({
  items,
  players,
  unknownPlayerLabel,
  chatLastReadIncomingEventIndex,
  unreadIncomingCount,
  onRequestMarkReadToIncomingEventIndex,
}) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const effectivePlayers = players ?? [];

  const isNearBottomRef = useRef(true);
  const isProgrammaticScrollRef = useRef(false);
  const lastRequestedReadIncomingEventIndexRef = useRef(chatLastReadIncomingEventIndex);
  const readVisibilityCheckRafRef = useRef<number | null>(null);
  const didApplyInitialScrollRef = useRef(false);
  const [isNearBottom, setIsNearBottom] = useState(true);

  const wantsJumpButton = unreadIncomingCount > 0 && !isNearBottom;
  const [isJumpButtonMounted, setIsJumpButtonMounted] = useState(false);
  const [isJumpButtonShown, setIsJumpButtonShown] = useState(false);
  const hideJumpButtonTimerRef = useRef<number | null>(null);

  const computeIsNearBottom = useCallback(
    (el: HTMLDivElement) =>
      el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_THRESHOLD_PX,
    [],
  );

  /** Y bounds of the scrollable *client* area (excludes composer; only this div scrolls). */
  const getScrollViewportYBounds = useCallback((el: HTMLDivElement) => {
    const r = el.getBoundingClientRect();
    const vpTop = r.top + el.clientTop;
    const vpBottom = vpTop + el.clientHeight;
    return { vpTop, vpBottom };
  }, []);

  const computeNextReadIndexFromFullyVisibleIncoming = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return null;

    const { vpTop, vpBottom } = getScrollViewportYBounds(el);
    const eps = 1;

    // Which incoming message rows are *fully* inside the scroll viewport?
    const fullyVisibleIncoming = new Set<number>();
    const incomingNodeList = el.querySelectorAll<HTMLElement>(
      '[data-chat-incoming="true"][data-chat-event-log-index]',
    );
    incomingNodeList.forEach((node) => {
      const idxStr = node.getAttribute('data-chat-event-log-index');
      if (!idxStr) return;
      const idx = Number(idxStr);
      if (!Number.isFinite(idx)) return;

      const rect = node.getBoundingClientRect();
      const fullyVisible = rect.top >= vpTop - eps && rect.bottom <= vpBottom + eps;
      if (fullyVisible) fullyVisibleIncoming.add(idx);
    });

    // Advance only contiguously from the current read index.
    const lastRead = lastRequestedReadIncomingEventIndexRef.current;
    const incomingIndices = items
      .filter(
        (
          item,
        ): item is ChatDisplayItem & {
          type: 'message';
          model: NonNullable<ChatDisplayItem['model']>;
        } =>
          item.type === 'message' &&
          !!item.model &&
          item.model.direction === 'incoming' &&
          typeof item.eventLogIndex === 'number',
      )
      .map((item) => item.eventLogIndex as number)
      .sort((a, b) => a - b);

    let nextReadIndex: number | null = null;
    for (const idx of incomingIndices) {
      if (idx <= lastRead) continue;

      if (fullyVisibleIncoming.has(idx)) {
        nextReadIndex = idx;
      } else {
        break; // Stop at the first unread incoming message that's not fully visible.
      }
    }

    return nextReadIndex;
  }, [getScrollViewportYBounds, items]);

  const requestReadAdvance = useCallback(
    (nextIndex: number) => {
      const lastReadRequested = lastRequestedReadIncomingEventIndexRef.current;
      if (nextIndex <= lastReadRequested) return;

      lastRequestedReadIncomingEventIndexRef.current = nextIndex;
      onRequestMarkReadToIncomingEventIndex?.(nextIndex);
    },
    [onRequestMarkReadToIncomingEventIndex],
  );

  const scheduleVisibilityReadCheck = useCallback(() => {
    if (readVisibilityCheckRafRef.current) return;
    readVisibilityCheckRafRef.current = requestAnimationFrame(() => {
      readVisibilityCheckRafRef.current = null;
      const nextIndex = computeNextReadIndexFromFullyVisibleIncoming();
      if (nextIndex !== null) requestReadAdvance(nextIndex);
    });
  }, [computeNextReadIndexFromFullyVisibleIncoming, requestReadAdvance]);

  useEffect(() => {
    lastRequestedReadIncomingEventIndexRef.current = chatLastReadIncomingEventIndex;
  }, [chatLastReadIncomingEventIndex]);

  useEffect(() => {
    return () => {
      if (readVisibilityCheckRafRef.current) {
        cancelAnimationFrame(readVisibilityCheckRafRef.current);
        readVisibilityCheckRafRef.current = null;
      }
    };
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    isProgrammaticScrollRef.current = true;
    el.scrollTop = el.scrollHeight;
    const nextNearBottom = computeIsNearBottom(el);
    isNearBottomRef.current = nextNearBottom;
    setIsNearBottom(nextNearBottom);

    // Allow one scroll event tick (if the browser/jsdom emits it) to be treated as programmatic.
    requestAnimationFrame(() => {
      isProgrammaticScrollRef.current = false;
      const nextIndex = computeNextReadIndexFromFullyVisibleIncoming();
      if (nextIndex !== null) requestReadAdvance(nextIndex);
    });
  }, [computeIsNearBottom, computeNextReadIndexFromFullyVisibleIncoming, requestReadAdvance]);

  /**
   * On first mount: if there are unread incoming messages, do not assume "near bottom" (avoids
   * auto-scrolling to latest and incorrectly clearing unread). If there is no unread, scroll to
   * bottom so the latest thread is visible (composer is a sibling below this scroll area only).
   */
  useLayoutEffect(() => {
    if (didApplyInitialScrollRef.current) return;
    didApplyInitialScrollRef.current = true;
    const el = scrollRef.current;
    if (!el) return;
    if (unreadIncomingCount > 0) {
      const near = computeIsNearBottom(el);
      isNearBottomRef.current = near;
      setIsNearBottom(near);
    } else {
      el.scrollTop = el.scrollHeight;
      isNearBottomRef.current = true;
      setIsNearBottom(true);
    }
  }, [unreadIncomingCount, computeIsNearBottom]);

  const updateNearBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const nextNearBottom = computeIsNearBottom(el);
    if (isNearBottomRef.current !== nextNearBottom) {
      isNearBottomRef.current = nextNearBottom;
      setIsNearBottom(nextNearBottom);
    }
  }, [computeIsNearBottom]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const nextNearBottom = computeIsNearBottom(el);
    const prevNearBottom = isNearBottomRef.current;
    if (prevNearBottom !== nextNearBottom) {
      isNearBottomRef.current = nextNearBottom;
      setIsNearBottom(nextNearBottom);
    }

    if (!isProgrammaticScrollRef.current) {
      // Only mark read based on actual fully-visible incoming messages.
      scheduleVisibilityReadCheck();
    }
  }, [computeIsNearBottom, scheduleVisibilityReadCheck]);

  // Autoscroll: if user is near bottom (before render), scroll to bottom on new items.
  useEffect(() => {
    if (!isNearBottomRef.current) return;
    const raf = requestAnimationFrame(() => scrollToBottom());
    return () => cancelAnimationFrame(raf);
  }, [items.length, scrollToBottom]);

  // Initial near-bottom sync after mount/layout.
  useEffect(() => {
    const raf = requestAnimationFrame(() => updateNearBottom());
    return () => cancelAnimationFrame(raf);
  }, [updateNearBottom]);

  useEffect(() => {
    if (wantsJumpButton) {
      if (hideJumpButtonTimerRef.current) {
        window.clearTimeout(hideJumpButtonTimerRef.current);
        hideJumpButtonTimerRef.current = null;
      }
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsJumpButtonMounted(true);
      setIsJumpButtonShown(true);
      return;
    }

    if (!isJumpButtonMounted) return;

    // Trigger shrink/fade out before unmounting.
    setIsJumpButtonShown(false);
    hideJumpButtonTimerRef.current = window.setTimeout(() => {
      setIsJumpButtonMounted(false);
      hideJumpButtonTimerRef.current = null;
    }, JUMP_BUTTON_OUT_ANIM_MS);

    return () => {
      if (hideJumpButtonTimerRef.current) {
        window.clearTimeout(hideJumpButtonTimerRef.current);
        hideJumpButtonTimerRef.current = null;
      }
    };
  }, [wantsJumpButton, isJumpButtonMounted]);

  useEffect(() => {
    return () => {
      if (!hideJumpButtonTimerRef.current) return;
      window.clearTimeout(hideJumpButtonTimerRef.current);
      hideJumpButtonTimerRef.current = null;
    };
  }, []);

  return (
    <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
      <div
        ref={scrollRef}
        data-testid="chat-message-list-scroll"
        className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto"
        onScroll={handleScroll}
      >
        <ChatMessageListScrollContext.Provider value={scrollRef}>
          <Tooltip.Provider delayDuration={200}>
            <div ref={listRef} className={cn('flex flex-col gap-0.5 @[18rem]:gap-1')}>
              {buildDisplayBlocks(items).map((block) => {
                if (block.kind === 'event') {
                  return (
                    <div key={block.key}>
                      <ChatEventMessage item={block.item} players={effectivePlayers} />
                    </div>
                  );
                }
                return (
                  <ChatMessageGroup
                    key={block.key}
                    messages={block.messages}
                    unknownPlayerLabel={unknownPlayerLabel}
                  />
                );
              })}
            </div>
          </Tooltip.Provider>
        </ChatMessageListScrollContext.Provider>
      </div>

      {isJumpButtonMounted ? (
        <button
          type="button"
          onClick={() => scrollToBottom()}
          className={cn(
            'pointer-events-auto absolute right-2 bottom-2 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-[#405820] text-white shadow-lg backdrop-blur',
            isJumpButtonShown ? 'animate-chat-jump-in' : 'animate-chat-jump-out',
          )}
          aria-label={t('chat_jump_to_latest')}
        >
          <span className="relative inline-flex">
            <ChevronDownIcon className="h-4 w-4" aria-hidden />
            <div
              className="absolute -top-[17px] -left-[15px] flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[0.65rem] font-bold text-white shadow"
              aria-hidden
            >
              {unreadIncomingCount > 99 ? '99+' : unreadIncomingCount}
            </div>
          </span>
        </button>
      ) : null}
    </div>
  );
};

export default ChatMessageList;
